import { Select, SelectMenuItem } from '@execonline-inc/execonline-ui';
import { useQueryClient } from '@tanstack/react-query';
import { just, Maybe, nothing } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import { NotTranslated, T } from '../../../../Translations';
import { Link } from '../../../../Resource/Types';
import { assignDomain, serverErrorMessage } from '../../Api';
import { ConnectionSummary, LoginDomain } from '../../Types';

interface Props {
  loginDomain: LoginDomain;
  connections: ReadonlyArray<ConnectionSummary>;
  links: ReadonlyArray<Link>;
}

type State = { kind: 'idle' } | { kind: 'saving' } | { kind: 'error'; message: Maybe<string> };

const idle = (): State => ({ kind: 'idle' });
const saving = (): State => ({ kind: 'saving' });
const error = (message: Maybe<string>): State => ({ kind: 'error', message });

const passwordValue = 'password';

const DomainRow: React.FC<Props> = ({ loginDomain, connections, links }) => {
  const [state, setState] = React.useState<State>(idle());
  const queryClient = useQueryClient();

  const currentValue = loginDomain.ssoConnectionId
    .map((id) => `${id}`)
    .getOrElseValue(passwordValue);

  // With no connections there is nothing to change a domain to, so the
  // sign-in method renders as plain text (mockup screen 1).
  if (connections.length === 0) {
    return (
      <tr>
        <td className="border-b border-border py-3 pr-3 align-middle text-sm font-medium text-foreground">
          {loginDomain.domain}
        </td>
        <td className="border-b border-border py-3 align-middle text-sm text-muted-foreground">
          <T kind="Password sign-in" />
        </td>
      </tr>
    );
  }

  const onChange = (value: string) => {
    const connectionId: Maybe<number> = value === passwordValue ? nothing() : just(Number(value));

    setState(saving());
    assignDomain(links, loginDomain.id, connectionId).fork(
      (err) => setState(error(serverErrorMessage(err))),
      () => {
        setState(idle());
        queryClient.invalidateQueries({ queryKey: ['sso-configurations'] });
      },
    );
  };

  return (
    <tr>
      <td className="border-b border-border py-3 pr-3 align-middle text-sm font-medium text-foreground">
        {loginDomain.domain}
      </td>
      <td
        className="border-b border-border py-3 align-middle"
        data-intercom-target="sso-domain-sign-in-method-dropdown"
      >
        <Select
          multiple={false}
          value={currentValue}
          onChange={onChange}
          renderValue={(value) =>
            value === passwordValue ? (
              <T kind="Password sign-in" />
            ) : (
              connections
                .filter((connection) => `${connection.id}` === value)
                .map((connection) => (
                  <NotTranslated key={connection.id} text={connection.displayName} />
                ))
            )
          }
          disabled={state.kind === 'saving'}
          aria-label={`Sign-in method for ${loginDomain.domain}`}
        >
          <SelectMenuItem value={passwordValue} selected={currentValue === passwordValue}>
            <T kind="Password sign-in" />
          </SelectMenuItem>
          {connections.map((connection) => (
            <SelectMenuItem
              key={connection.id}
              value={`${connection.id}`}
              disabled={connection.status !== 'active'}
              selected={currentValue === `${connection.id}`}
            >
              {connection.status === 'active' ? (
                <NotTranslated text={connection.displayName} />
              ) : (
                <>
                  <NotTranslated text={connection.displayName} /> <T kind="(setup incomplete)" />
                </>
              )}
            </SelectMenuItem>
          ))}
        </Select>
        {state.kind === 'error' && (
          <p className="mt-1 text-xs text-destructive">
            {state.message
              .map((message) => <NotTranslated key="server-error" text={message} />)
              .getOrElse(() => (
                <T kind="We are unable to process your request at this time" />
              ))}
          </p>
        )}
      </td>
    </tr>
  );
};

export default observer(DomainRow);
